import { ProgressBreadcrumbCompletedDisplayStyle, ProgressBreadcrumbList, ProgressBreadcrumbListTheme } from '@apply/components';
import { IncompleteStepAlertDetection, WizardStep, WizardStepNavigation } from '../constants/types';
// Admin pages don't load the shared components stylesheet, so pull in the breadcrumb styles directly.
import '@apply/components/src/scss/controls/_progressbreadcrumblist.scss';

export interface WizardStepsProps {
    id: string;
    /** Steps in display order. */
    steps: WizardStep[];
    /** Number of the current step. */
    currentStep: number;
    /** Called with the step's number when a navigable step is clicked. */
    onStepClick?: (stepNumber: number) => void;
    /** Which steps can be clicked. Defaults to 'previous'. */
    navigation?: WizardStepNavigation;
    /** Which incomplete steps show a red warning bubble. Defaults to None. */
    incompleteStepAlertDetection?: IncompleteStepAlertDetection;
}

const isStepNavigable = (stepNumber: number, currentStep: number, navigation: WizardStepNavigation) => {
    switch (navigation) {
        case 'all':
            return stepNumber !== currentStep;
        case 'previous':
            return stepNumber < currentStep;
        default:
            return false;
    }
};

/**
 * Step list for WizardModal's sidebar. Steps before the current one are shown as completed.
 * Clicking a navigable step (its bubble or label) calls onStepClick with that step's number; disabled steps are greyed
 * out and never navigable.
 * Every step's progressBarInfo is passed through (unless its total is 0); ProgressBreadcrumbList shows it under the
 * current step only. With incompleteStepAlertDetection, the chosen steps whose progressBarInfo isn't complete show a red
 * warning bubble instead.
 */
export const WizardSteps = ({
    id,
    steps,
    currentStep,
    onStepClick,
    navigation = 'previous',
    incompleteStepAlertDetection = IncompleteStepAlertDetection.None,
}: WizardStepsProps) => {
    // The bubbles show each step's position (1, 2, 3...), not its number, so a wizard that leaves a step out (its
    // numbers then skip one) still counts up evenly. Clicks still report the step's number.
    const currentPosition = steps.findIndex((step) => step.number === currentStep) + 1;

    const isAlertDetected = (position: number) => {
        switch (incompleteStepAlertDetection) {
            case IncompleteStepAlertDetection.All:
                return position !== currentPosition;
            case IncompleteStepAlertDetection.Previous:
                return position < currentPosition;
            default:
                return false;
        }
    };

    const crumbs = steps.map((step, index) => {
        const isNavigable = !!onStepClick && !step.disabled && isStepNavigable(step.number, currentStep, navigation);
        // A total of 0 means nothing is required right now, so the step can't be incomplete
        const remaining = step.progressBarInfo?.total ? step.progressBarInfo.total - step.progressBarInfo.numberComplete : 0;

        return {
            id: `${id}_crumb_${step.number}`,
            number: index + 1,
            children: step.label,
            enabled: isNavigable,
            disabled: step.disabled,
            handleClick: isNavigable ? () => onStepClick?.(step.number) : undefined,
            // Nothing to complete (no required fields right now) means no progress bar
            progressBarInfo: step.progressBarInfo?.total ? step.progressBarInfo : undefined,
            hasError: remaining > 0 && isAlertDetected(index + 1),
            errorHint: `${remaining} required ${remaining === 1 ? 'field still needs' : 'fields still need'} to be filled in`,
        };
    });

    return (
        <ProgressBreadcrumbList
            id={id}
            theme={ProgressBreadcrumbListTheme.Blue}
            activeCrumbNumber={currentPosition}
            crumbs={crumbs}
            completedCrumbDisplayStyle={ProgressBreadcrumbCompletedDisplayStyle.Checkmark}
        />
    );
};
